/**
 * Map filters store
 * Manages filter state for the Maps page
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Map filters state interface
 */
interface MapFiltersState {
  // Filter values
  jobId: string | null;
  category: string[]; // Supports multiple categories
  country: string[]; // Supports multiple countries (default: ['Germany'])
  minRating: number | null;
  searchQuery: string;
  searchTerm: string[]; // Filter by original search term that found places (supports multiple)

  // UI state
  selectedPlaceId: string | null;
  clusteringEnabled: boolean;
  clusterRadius: number; // Clustering radius in pixels (default 50)

  // Actions
  setJobId: (jobId: string | null) => void;
  setCategory: (category: string[]) => void;
  setCountry: (country: string[]) => void;
  setMinRating: (rating: number | null) => void;
  setSearchQuery: (query: string) => void;
  setSearchTerm: (term: string[]) => void;
  setSelectedPlace: (placeId: string | null) => void;
  setClusteringEnabled: (enabled: boolean) => void;
  setClusterRadius: (radius: number) => void;
  clearFilters: () => void;
  hasActiveFilters: () => boolean;
}

/**
 * Initial state
 */
const initialFilters = {
  jobId: null,
  category: [] as string[],
  country: ['Germany'] as string[],
  minRating: null,
  searchQuery: '',
  searchTerm: [] as string[],
  selectedPlaceId: null,
  clusteringEnabled: true, // On by default - required for performance with large datasets
  clusterRadius: 50, // Default clustering radius in pixels
};

/**
 * Map filters store with persistence
 */
export const useMapFiltersStore = create<MapFiltersState>()(
  persist(
    (set, get) => ({
      ...initialFilters,

      setJobId: (jobId) => set({ jobId }),

      setCategory: (category) => set({ category }),

      setCountry: (country) => set({ country }),

      setMinRating: (minRating) => set({ minRating }),

      setSearchQuery: (searchQuery) => set({ searchQuery }),

      setSearchTerm: (searchTerm) => set({ searchTerm }),

      setSelectedPlace: (selectedPlaceId) => set({ selectedPlaceId }),

      setClusteringEnabled: (clusteringEnabled) => set({ clusteringEnabled }),

      setClusterRadius: (clusterRadius) => set({ clusterRadius }),

      clearFilters: () =>
        set({
          jobId: null,
          category: [],
          country: ['Germany'],
          minRating: null,
          searchQuery: '',
          searchTerm: [],
        }),

      hasActiveFilters: () => {
        const state = get();
        // Country is only "active" if it's not the default ['Germany']
        const isNonDefaultCountry = state.country &&
          (state.country.length !== 1 || state.country[0] !== 'Germany');
        return !!(
          state.jobId ||
          (state.category && state.category.length > 0) ||
          isNonDefaultCountry ||
          state.minRating ||
          state.searchQuery ||
          (state.searchTerm && state.searchTerm.length > 0)
        );
      },
    }),
    {
      name: 'maps-scraper-map-filters',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        jobId: state.jobId,
        category: state.category,
        country: state.country,
        minRating: state.minRating,
        searchTerm: state.searchTerm,
        clusteringEnabled: state.clusteringEnabled,
        clusterRadius: state.clusterRadius,
        // Don't persist searchQuery or selectedPlaceId
      }),
      // Ensure arrays are properly initialized when hydrating from storage
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<MapFiltersState>),
        // Ensure arrays are never null
        category: (persistedState as Partial<MapFiltersState>)?.category ?? [],
        country: (persistedState as Partial<MapFiltersState>)?.country ?? ['Germany'],
        searchTerm: (persistedState as Partial<MapFiltersState>)?.searchTerm ?? [],
      }),
    }
  )
);

/**
 * Hook to get filter values as PlaceFilter object
 */
export function useMapFilterValues() {
  const jobId = useMapFiltersStore((state) => state.jobId);
  const category = useMapFiltersStore((state) => state.category);
  const country = useMapFiltersStore((state) => state.country);
  const minRating = useMapFiltersStore((state) => state.minRating);
  const searchQuery = useMapFiltersStore((state) => state.searchQuery);
  const searchTerm = useMapFiltersStore((state) => state.searchTerm);

  return {
    jobId: jobId || undefined,
    category: category && category.length > 0 ? category : undefined,
    country: country && country.length > 0 ? country : undefined,
    minRating: minRating || undefined,
    search: searchQuery || undefined,
    searchTerm: searchTerm && searchTerm.length > 0 ? searchTerm : undefined,
  };
}

export default useMapFiltersStore;
